import { useState, useEffect } from "react";

const API_BASE = "https://api.weatherdatamanager.gwc.dm-airtech.com";

const C = {
  bg:"#0f1117",surface:"#181c25",surfaceAlt:"#1e2330",border:"#2a3045",
  borderHi:"#3a4460",text:"#e8ecf4",textSub:"#7a8499",textMuted:"#4a5268",
  accent:"#4f8ef7",accentDim:"#1e3a6e",success:"#3ecf8e",successDim:"#0d3d28",
  warn:"#f5a623",warnDim:"#3d2a0a",danger:"#f05252",dangerDim:"#3d1010",
};

const api = async (path, opts = {}) => {
  const r = await fetch(`${API_BASE}${path}`, { headers: { "Content-Type": "application/json" }, ...opts });
  if (!r.ok) { const err = await r.json().catch(() => ({ detail: r.statusText })); throw new Error(err.detail || r.statusText); }
  return r;
};
const apiJson = async (path, opts = {}) => (await api(path, opts)).json();

const pill = (label, color = C.accent, bg = C.accentDim) => (
  <span style={{ fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:20, background:bg, color, letterSpacing:0.4 }}>{label}</span>
);
const Card = ({ children, style = {} }) => (
  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"1rem 1.125rem", ...style }}>{children}</div>
);
const Label = ({ children }) => (
  <label style={{ fontSize:12, color:C.textSub, display:"block", marginBottom:4 }}>{children}</label>
);
const Err = ({ msg }) => msg ? (
  <div style={{ background:C.dangerDim, border:`1px solid ${C.danger}`, borderRadius:8, padding:"10px 14px", fontSize:13, color:C.danger, marginBottom:12 }}>{msg}</div>
) : null;
const Spinner = () => (
  <span style={{ display:"inline-block", width:14, height:14, border:`2px solid ${C.border}`, borderTopColor:C.accent, borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
);

const TRAIN_STEPS = ["Location & dates", "Channel mapping", "Export"];

function StepIndicator({ step }) {
  return (
    <div style={{ display:"flex", gap:0, marginBottom:24 }}>
      {TRAIN_STEPS.map((label, i) => (
        <div key={i} style={{ flex:1, textAlign:"center" }}>
          <div style={{ height:2, background:i<=step?C.accent:C.border, marginBottom:6, borderRadius:2 }} />
          <span style={{ fontSize:11, color:i===step?C.accent:C.textSub, fontWeight:i===step?600:400 }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

function TrainingStep1({ onNext }) {
  const [lat,setLat]=useState("51.2194");
  const [lon,setLon]=useState("4.4025");
  const [start,setStart]=useState("2026-06-10");
  const [end,setEnd]=useState("2026-06-12");
  const [source,setSource]=useState("");
  const [stId,setStId]=useState("");
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");

  async function handleFetch() {
    setLoading(true); setError("");
    try {
      const body = { lat:+lat, lon:+lon, start, end };
      if (source) body.source = source;
      if (stId)   body.station_id = stId;
      const data = await apiJson("/pipeline/preview", { method:"POST", body:JSON.stringify(body) });
      if (!data.ok) throw new Error(data.detail || "Preview failed");
      onNext({ lat:+lat, lon:+lon, start, end, preview:data });
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }

  return (
    <div>
      <p style={{ color:C.textSub, fontSize:13, marginBottom:16 }}>
        Enter a location and date range. The system finds the nearest weather station with historical data and fetches matching Open-Meteo forecast for the same period.
      </p>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
        <div><Label>Latitude</Label><input value={lat} onChange={e=>setLat(e.target.value)} style={{ width:"100%" }} placeholder="51.2194" /></div>
        <div><Label>Longitude</Label><input value={lon} onChange={e=>setLon(e.target.value)} style={{ width:"100%" }} placeholder="4.4025" /></div>
        <div><Label>Start date</Label><input type="date" value={start} onChange={e=>setStart(e.target.value)} style={{ width:"100%" }} /></div>
        <div><Label>End date</Label><input type="date" value={end} onChange={e=>setEnd(e.target.value)} style={{ width:"100%" }} /></div>
      </div>
      <details style={{ marginBottom:16 }}>
        <summary style={{ fontSize:12, color:C.textSub, marginBottom:8 }}>▶ Override source / station (optional)</summary>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, paddingTop:8 }}>
          <div><Label>Source</Label><input value={source} onChange={e=>setSource(e.target.value)} style={{ width:"100%" }} placeholder="rmi  dwd  knmi  metar  nws" /></div>
          <div><Label>Station ID</Label><input value={stId} onChange={e=>setStId(e.target.value)} style={{ width:"100%" }} placeholder="e.g. 6438  KJFK" /></div>
        </div>
      </details>
      <Err msg={error} />
      <button className="primary" onClick={handleFetch} disabled={loading} style={{ width:"100%" }}>
        {loading ? <><Spinner /> Fetching data…</> : "Fetch data →"}
      </button>
    </div>
  );
}

function MappingTable({ title, fields, mlChannels, allUnits, mapping, setMapping, excluded, setExcluded }) {
  const excludedCount = fields.filter(f=>excluded[f.field_name]).length;
  const mappedCount   = fields.filter(f=>!excluded[f.field_name]&&mapping[f.field_name]?.to).length;
  return (
    <Card style={{ marginBottom:12 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
        <p style={{ fontSize:13, fontWeight:600, color:C.textSub }}>
          {title} — {fields.length} fields
          <span style={{ marginLeft:10, fontWeight:400, fontSize:11 }}>
            <span style={{ color:C.success }}>{mappedCount} mapped</span>
            {excludedCount>0&&<span style={{ color:C.textMuted, marginLeft:8 }}>{excludedCount} excluded</span>}
          </span>
        </p>
        <button onClick={() => {
          const allEx = fields.every(f=>excluded[f.field_name]);
          const next = {};
          fields.forEach(f => { next[f.field_name]=!allEx; });
          setExcluded(ex=>({...ex,...next}));
        }} style={{ fontSize:11, padding:"3px 10px", color:C.textSub }}>
          {fields.every(f=>excluded[f.field_name])?"Include all":"Exclude all"}
        </button>
      </div>
      <div style={{ overflowX:"auto" }}>
        <table>
          <thead><tr>{["Include","Source field","Sample","Unit","","ML channel","Conversion"].map((h,i)=><th key={i}>{h}</th>)}</tr></thead>
          <tbody>
            {fields.map(f => {
              const isEx = !!excluded[f.field_name];
              const m = mapping[f.field_name]||{to:"",source_unit:f.suggested_unit||""};
              const tgt = mlChannels.find(c=>c.id===m.to);
              const conv = tgt&&m.source_unit&&m.source_unit!==tgt.unit?`${m.source_unit} → ${tgt.unit}`:tgt?"✓":"";
              const convColor = conv==="✓"?C.success:C.warn;
              return (
                <tr key={f.field_name} style={{ opacity:isEx?0.35:1 }}>
                  <td style={{ textAlign:"center" }}>
                    <input type="checkbox" checked={!isEx}
                      onChange={e=>setExcluded(ex=>({...ex,[f.field_name]:!e.target.checked}))}
                      style={{ cursor:"pointer", width:14, height:14 }} />
                  </td>
                  <td style={{ fontWeight:500, color:isEx?C.textMuted:C.text }}>{f.field_name}</td>
                  <td style={{ color:C.textSub, fontFamily:"JetBrains Mono,monospace", fontSize:11 }}>
                    {f.sample_value!=null?(typeof f.sample_value==="number"?f.sample_value.toFixed(2):f.sample_value):"—"}
                  </td>
                  <td>
                    <select disabled={isEx} value={m.source_unit} onChange={e=>setMapping(mp=>({...mp,[f.field_name]:{...m,source_unit:e.target.value}}))} style={{ fontSize:11, width:80 }}>
                      <option value="">—</option>
                      {allUnits.map(u=><option key={u} value={u}>{u||"(none)"}</option>)}
                    </select>
                  </td>
                  <td style={{ color:C.textMuted }}>→</td>
                  <td>
                    <select disabled={isEx} value={m.to} onChange={e=>setMapping(mp=>({...mp,[f.field_name]:{...m,to:e.target.value}}))} style={{ fontSize:11, width:150 }}>
                      <option value="">— channel —</option>
                      {mlChannels.map(c=><option key={c.id} value={c.id}>{c.id} ({c.unit})</option>)}
                    </select>
                  </td>
                  <td style={{ fontSize:11, color:isEx?C.textMuted:convColor, fontWeight:conv==="✓"?500:400 }}>
                    {isEx?"excluded":conv}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function TrainingStep2({ state, onNext, onBack }) {
  const { preview } = state;
  const [sensorMap,setSensorMap]=useState({});
  const [forecastMap,setForecastMap]=useState({});
  const [sensorExcluded,setSensorExcluded]=useState({});
  const [forecastExcluded,setForecastExcluded]=useState({});
  const [mlChannels,setMlChannels]=useState([]);
  const [allUnits,setAllUnits]=useState([]);

  useEffect(() => {
    apiJson("/channels").then(d=>{if(d.flat)setMlChannels(d.flat);}).catch(()=>{});
    apiJson("/channels/units").then(d=>{if(d.all)setAllUnits(d.all);}).catch(()=>{});
    const initMap = s => { const m={}; (s||[]).forEach(x=>{if(x.to)m[x.from]={to:x.to,source_unit:x.source_unit||""};}); return m; };
    const initEx  = (fields,s) => {
      const mapped=new Set((s||[]).filter(x=>x.to).map(x=>x.from));
      const ex={};
      (fields||[]).forEach(f=>{ if(!mapped.has(f.field_name)&&f.completeness===0)ex[f.field_name]=true; });
      return ex;
    };
    setSensorMap(initMap(preview.sensor.suggested_mapping));
    setForecastMap(initMap(preview.forecast.suggested_mapping));
    setSensorExcluded(initEx(preview.sensor.fields,preview.sensor.suggested_mapping));
    setForecastExcluded(initEx(preview.forecast.fields,preview.forecast.suggested_mapping));
  }, []);

  const buildList = (mp,ex) => Object.entries(mp).filter(([f,v])=>v.to&&!ex[f]).map(([from,v])=>({from_field:from,to_channel:v.to,source_unit:v.source_unit||""}));

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
        <Card style={{ background:C.surfaceAlt }}>
          <p style={{ fontSize:12, color:C.textSub }}>
            <span style={{ color:C.text, fontWeight:600 }}>{preview.sensor.source?.toUpperCase()}</span>
            {" · "}{preview.sensor.station_id}
            {preview.sensor.distance_km&&` · ${preview.sensor.distance_km.toFixed(1)} km`}
            {" · "}<span style={{ color:C.success }}>{preview.sensor.rows} rows</span>
          </p>
        </Card>
        <Card style={{ background:C.surfaceAlt }}>
          <p style={{ fontSize:12, color:C.textSub }}>
            <span style={{ color:C.text, fontWeight:600 }}>Open-Meteo</span>{" · "}NWP historical forecast
            {" · "}<span style={{ color:C.success }}>{preview.forecast.rows} rows</span>
          </p>
        </Card>
      </div>
      <MappingTable title="Sensor data" fields={preview.sensor.fields||[]} mlChannels={mlChannels} allUnits={allUnits} mapping={sensorMap} setMapping={setSensorMap} excluded={sensorExcluded} setExcluded={setSensorExcluded} />
      <MappingTable title="Forecast data" fields={preview.forecast.fields||[]} mlChannels={mlChannels} allUnits={allUnits} mapping={forecastMap} setMapping={setForecastMap} excluded={forecastExcluded} setExcluded={setForecastExcluded} />
      <div style={{ display:"flex", gap:8 }}>
        <button onClick={onBack} style={{ flex:1 }}>← Back</button>
        <button className="primary" onClick={()=>onNext({...state,sensorMapping:buildList(sensorMap,sensorExcluded),forecastMapping:buildList(forecastMap,forecastExcluded)})} style={{ flex:2 }}>
          Preview & export →
        </button>
      </div>
    </div>
  );
}

function TrainingStep3({ state, onBack }) {
  const { preview, sensorMapping, forecastMapping, lat, lon, start, end } = state;
  const [downloading,setDownloading]=useState("");
  const [error,setError]=useState("");

  async function download(type) {
    setDownloading(type); setError("");
    try {
      let path, body, filename;
      if(type==="sensor"){
        path="/pipeline/export/sensor"; body={lat,lon,start,end,mapping:sensorMapping,interval_seconds:600}; filename=`sensor_${start}_${end}.parquet`;
      } else if(type==="forecast"){
        path="/pipeline/export/forecast"; body={lat,lon,start,end,mapping:forecastMapping,interval_seconds:3600}; filename=`forecast_${start}_${end}.parquet`;
      } else {
        path="/pipeline/training"; body={lat,lon,start,end,sensor_mapping:sensorMapping,forecast_mapping:forecastMapping}; filename=`training_${start}_${end}.zip`;
      }
      const r = await api(path,{method:"POST",body:JSON.stringify(body)});
      const blob = await r.blob();
      const a = document.createElement("a"); a.href=URL.createObjectURL(blob); a.download=filename; a.click(); URL.revokeObjectURL(a.href);
    } catch(e){ setError(e.message); }
    finally { setDownloading(""); }
  }

  const sensorMapped   = sensorMapping.filter(m=>m.to_channel).length;
  const forecastMapped = forecastMapping.filter(m=>m.to_channel).length;

  const SampleTable = ({ rows, label }) => {
    if(!rows?.length) return null;
    const cols = Object.keys(rows[0]).filter(k=>!["source","station_id","station_name","fetched_at"].includes(k)).slice(0,7);
    return (
      <div style={{ marginBottom:12 }}>
        <p style={{ fontSize:11, color:C.textSub, marginBottom:6 }}>{label}</p>
        <div style={{ overflowX:"auto" }}>
          <table>
            <thead><tr>{cols.map(c=><th key={c}>{c}</th>)}</tr></thead>
            <tbody>
              {rows.slice(0,3).map((row,i)=>(
                <tr key={i}>{cols.map(c=>(
                  <td key={c} style={{ fontFamily:"JetBrains Mono,monospace", fontSize:11 }}>
                    {row[c]==null?<span style={{ color:C.textMuted }}>—</span>:typeof row[c]==="number"?row[c].toFixed(3):String(row[c]).slice(0,22)}
                  </td>
                ))}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:16 }}>
        {[
          {label:"Sensor rows",value:preview.sensor.rows},
          {label:"Forecast rows",value:preview.forecast.rows},
          {label:"Sensor channels",value:sensorMapped},
          {label:"Forecast channels",value:forecastMapped},
        ].map(({label,value})=>(
          <Card key={label} style={{ background:C.surfaceAlt, textAlign:"center" }}>
            <p style={{ fontSize:10, color:C.textSub, marginBottom:4 }}>{label}</p>
            <p style={{ fontSize:24, fontWeight:600, color:C.accent }}>{value}</p>
          </Card>
        ))}
      </div>
      <Card style={{ marginBottom:16 }}>
        <SampleTable rows={preview.sensor.sample} label={`Sensor — ${preview.sensor.source} ${preview.sensor.station_id}`} />
        <SampleTable rows={preview.forecast.sample} label="Forecast — Open-Meteo NWP" />
      </Card>
      <Err msg={error} />
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:8 }}>
        <button onClick={()=>download("sensor")} disabled={!!downloading}>↓ sensor.parquet</button>
        <button onClick={()=>download("forecast")} disabled={!!downloading}>↓ forecast.parquet</button>
        <button className="primary" onClick={()=>download("training")} disabled={!!downloading}>↓ training.zip</button>
      </div>
      <button onClick={onBack} style={{ width:"100%" }}>← Change mapping</button>
    </div>
  );
}

export default function TrainingTab() {
  const [step,setStep]=useState(0);
  const [state,setState]=useState({});
  return (
    <div>
      <StepIndicator step={step} />
      {step===0&&<TrainingStep1 onNext={d=>{setState(d);setStep(1);}} />}
      {step===1&&<TrainingStep2 state={state} onNext={d=>{setState(d);setStep(2);}} onBack={()=>setStep(0)} />}
      {step===2&&<TrainingStep3 state={state} onBack={()=>setStep(1)} />}
    </div>
  );
}